/**
 * 全屏 WebView / 沉浸式面板下，顶栏会被系统状态栏遮挡。
 * 优先用 env(safe-area-inset-*)；部分 Android 宿主不回传时再按「近全屏」启发式补齐。
 */

function measureSafeInset(side: 'top' | 'bottom'): number {
  if (typeof document === 'undefined' || !document.body) return 0;
  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText =
    `position:fixed;visibility:hidden;pointer-events:none;${side}:0;` +
    `width:0;height:env(safe-area-inset-${side}, 0px);`;
  document.body.appendChild(el);
  const h = el.getBoundingClientRect().height;
  el.remove();
  return Number.isFinite(h) ? h : 0;
}

function isMobileUa(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) return true;
  return navigator.maxTouchPoints > 0 && window.innerWidth < 900;
}

function isNearlyFullscreen(): boolean {
  const screenH = Math.max(window.screen?.height || 0, window.innerHeight || 0);
  if (screenH <= 0) return false;
  return window.innerHeight / screenH >= 0.9;
}

/** 写入 --beidou-safe-top / --beidou-safe-bottom（CSS px） */
export function applySafeAreaInsets(options?: { forceMobileFallback?: boolean }): void {
  const root = document.documentElement;
  const topEnv = measureSafeInset('top');
  const bottomEnv = measureSafeInset('bottom');
  const hostBottomRaw = root.style.getPropertyValue('--beidou-host-bottom').trim();
  const hostBottom = hostBottomRaw ? parseInt(hostBottomRaw, 10) : 0;
  const keyboardRaw = root.style.getPropertyValue('--beidou-keyboard-inset').trim();
  const keyboardInset = keyboardRaw ? parseInt(keyboardRaw, 10) : 0;
  const hostBottomEffective = keyboardInset >= 48 ? 0 : hostBottom;

  let topFb = 0;
  let bottomFb = 0;
  const allowFallback = Boolean(options?.forceMobileFallback) || window.parent !== window;
  if (allowFallback && isMobileUa() && isNearlyFullscreen()) {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (topEnv < 1) topFb = isIOS ? 47 : 40;
    if (bottomEnv < 1 && hostBottomEffective < 1) bottomFb = isIOS ? 20 : 12;
  }

  root.style.setProperty('--beidou-safe-top', `${Math.max(topEnv, topFb)}px`);
  root.style.setProperty(
    '--beidou-safe-bottom',
    `${Math.max(bottomEnv, bottomFb, hostBottomEffective > 0 ? hostBottomEffective : 0)}px`
  );
}

/** 挂载监听；返回卸载函数 */
export function installSafeAreaInsets(options?: { forceMobileFallback?: boolean }): () => void {
  const run = () => applySafeAreaInsets(options);
  run();
  window.addEventListener('resize', run);
  window.addEventListener('orientationchange', run);
  return () => {
    window.removeEventListener('resize', run);
    window.removeEventListener('orientationchange', run);
  };
}
